import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Typography } from '@mui/material';
import { YearlySpendingEntry } from '../../services/amsoApi';

interface YearlySpendingChartProps {
  yearlySpending: YearlySpendingEntry[];
  annualBudget: number;
  planningHorizon: number;
}

const STRATEGY_COLORS: Record<string, string> = {
  Replace: '#1e88e5',
  Rehabilitate: '#43a047',
  Repair: '#fb8c00',
  Inspect: '#8e24aa',
  Maintain: '#00acc1',
};
const FALLBACK_COLORS = ['#5c6bc0', '#ef5350', '#26a69a', '#d4e157', '#78909c'];

function buildStackedData(
  entries: YearlySpendingEntry[],
  planningHorizon: number,
): {
  categories: string[];
  strategies: string[];
  seriesMap: Record<string, number[]>;
} {
  const strategyCosts = new Map<string, Map<number, number>>();

  for (const { year, cost, strategy } of entries) {
    const strat = strategy ?? 'Other';
    if (!strategyCosts.has(strat)) strategyCosts.set(strat, new Map());
    const ym = strategyCosts.get(strat) as Map<number, number>;
    ym.set(year, (ym.get(year) ?? 0) + cost);
  }

  const strategies = Array.from(strategyCosts.keys()).sort();
  const categories: string[] = [];
  const seriesMap: Record<string, number[]> = {};
  for (const s of strategies) seriesMap[s] = [];

  for (let y = 1; y <= planningHorizon; y++) {
    categories.push(`Year ${y}`);
    for (const s of strategies) {
      seriesMap[s].push(strategyCosts.get(s)?.get(y) ?? 0);
    }
  }

  return { categories, strategies, seriesMap };
}

export default function YearlySpendingChart({
  yearlySpending,
  annualBudget,
  planningHorizon,
}: YearlySpendingChartProps) {
  const options = useMemo<Highcharts.Options>(() => {
    const { categories, strategies, seriesMap } = buildStackedData(
      yearlySpending,
      planningHorizon,
    );

    const totalBudget = annualBudget * planningHorizon;

    const yearTotals: number[] = [];
    const cumulativeTotals: number[] = [];
    let cumulative = 0;
    for (let i = 0; i < planningHorizon; i++) {
      let total = 0;
      for (const s of strategies) total += seriesMap[s][i];
      yearTotals.push(total);
      cumulative += total;
      cumulativeTotals.push(cumulative);
    }

    let colorIdx = 0;
    const series: Highcharts.SeriesColumnOptions[] = strategies.map(s => ({
      type: 'column' as const,
      name: s,
      data: seriesMap[s],
      color:
        STRATEGY_COLORS[s] ??
        FALLBACK_COLORS[colorIdx++ % FALLBACK_COLORS.length],
    }));

    return {
      chart: { type: 'column', height: 320, animation: { duration: 500 } },
      title: { text: undefined },
      xAxis: { categories, crosshair: true },
      yAxis: {
        title: { text: 'Spending ($)' },
        labels: {
          formatter() {
            const val = this.value as number;
            if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
            if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
            return `$${val}`;
          },
        },
        plotLines: [
          {
            value: annualBudget,
            color: '#e53935',
            dashStyle: 'Dash',
            width: 2,
            label: {
              text: `Budget: $${annualBudget.toLocaleString()}`,
              align: 'right',
              style: { color: '#e53935', fontWeight: 'bold' },
            },
            zIndex: 5,
          },
        ],
        stackLabels: {
          enabled: true,
          formatter() {
            const val = this.total ?? 0;
            if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
            if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
            if (val === 0) return '';
            return `$${val}`;
          },
          style: { fontWeight: 'bold', color: '#333', fontSize: '11px' },
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          borderRadius: 2,
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter() {
          const points = this.points ?? [];
          if (points.length === 0) return '';
          const yearIdx = (points[0] as any).point.index;
          const total = yearTotals[yearIdx] ?? 0;
          const cum = cumulativeTotals[yearIdx] ?? 0;
          const pctBudget = annualBudget > 0 ? (total / annualBudget) * 100 : 0;
          const cumPct = totalBudget > 0 ? (cum / totalBudget) * 100 : 0;

          let html = `<div style="padding:4px 0"><b>${points[0].key}</b></div>`;
          html += '<table style="border-spacing:4px 2px">';
          for (const pt of points) {
            if ((pt.y ?? 0) === 0) continue;
            html += `<tr>
              <td><span style="color:${pt.color}">\u25CF</span> ${
              pt.series.name
            }</td>
              <td style="text-align:right;padding-left:12px"><b>$${(
                pt.y ?? 0
              ).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}</b></td>
            </tr>`;
          }
          html += '</table>';
          html += `<div style="margin-top:6px;border-top:1px solid #eee;padding-top:4px">`;
          html += `<b>Total: $${total.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          })}</b> (${pctBudget.toFixed(1)}% of budget)<br/>`;
          html += `<span style="color:#666">Cumulative: $${cum.toLocaleString(
            undefined,
            { maximumFractionDigits: 0 },
          )} (${cumPct.toFixed(1)}% of total)</span>`;
          html += '</div>';
          return html;
        },
      },
      series,
      legend: { enabled: strategies.length > 1 },
      credits: { enabled: false },
    };
  }, [yearlySpending, annualBudget, planningHorizon]);

  if (!yearlySpending || yearlySpending.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No spending data available for this solution.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        updateArgs={[true, true, true]}
      />
    </Box>
  );
}
